import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Typography,
  Paper,
  IconButton,
  Chip,
  Alert,
} from '@mui/material';
import {
  NoteAddOutlined,
  EmailOutlined,
  PhoneOutlined,
  EventOutlined,
  CloseOutlined,
  SendOutlined,
  AutoAwesome,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import EmailBuilderDialog from './EmailBuilderDialog';

interface RecipientContext {
  name?: string;
  company?: string;
  email?: string;
}

export type ActivityType = 'NOTE' | 'EMAIL' | 'CALL' | 'MEETING';

export interface AddNoteFormData {
  type: ActivityType;
  subject?: string;
  content: string;
  metadata?: {
    emailSubject?: string;
    callDuration?: number;
    meetingDate?: Date;
  };
}

interface AddNoteFormProps {
  onSubmit: (data: AddNoteFormData) => void | Promise<void>;
  onCancel?: () => void;
  isSubmitting?: boolean;
  defaultType?: ActivityType;
  placeholder?: string;
  showTypeSelector?: boolean;
  compact?: boolean;
  recipientContext?: RecipientContext;
}

const activityTypes = [
  { value: 'NOTE', label: 'Note 備註', icon: <NoteAddOutlined /> },
  { value: 'CALL', label: 'Call 通話', icon: <PhoneOutlined /> },
  { value: 'MEETING', label: 'Meeting 會議', icon: <EventOutlined /> },
] as const;

const AddNoteForm: React.FC<AddNoteFormProps> = ({
  onSubmit,
  onCancel,
  isSubmitting = false,
  defaultType = 'NOTE',
  placeholder = 'Add a note, email summary, call log, or meeting details...',
  showTypeSelector = true,
  compact = false,
  recipientContext,
}) => {
  const [type, setType] = useState<ActivityType>(defaultType);
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [callDuration, setCallDuration] = useState<number | ''>('');
  const [meetingDate, setMeetingDate] = useState<Date | null>(null);
  const [error, setError] = useState<string>('');
  const [emailBuilderOpen, setEmailBuilderOpen] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!content.trim()) {
      setError('Please enter content 請輸入內容');
      return;
    }

    if (type === 'EMAIL' && !emailSubject.trim()) {
      setError('Please enter email subject 請輸入電郵主旨');
      return;
    }

    if (type === 'MEETING' && !meetingDate) {
      setError('Please select meeting date 請選擇會議日期');
      return;
    }

    const formData: AddNoteFormData = {
      type,
      subject: subject.trim() || undefined,
      content: content.trim(),
      metadata: {},
    };

    // Add type-specific metadata
    if (type === 'EMAIL') {
      formData.metadata!.emailSubject = emailSubject.trim();
    } else if (type === 'CALL' && callDuration) {
      formData.metadata!.callDuration = Number(callDuration);
    } else if (type === 'MEETING' && meetingDate) {
      formData.metadata!.meetingDate = meetingDate;
    }

    try {
      await onSubmit(formData);
      // Reset form
      setSubject('');
      setContent('');
      setEmailSubject('');
      setCallDuration('');
      setMeetingDate(null);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to add activity 新增活動失敗');
    }
  };

  const handleCancel = () => {
    setSubject('');
    setContent('');
    setEmailSubject('');
    setCallDuration('');
    setMeetingDate(null);
    setError('');
    onCancel?.();
  };

  const handleUseGeneratedEmail = (generatedSubject: string, generatedContent: string) => {
    setEmailSubject(generatedSubject);
    setContent(generatedContent);
  };

  const getTypeIcon = (activityType: ActivityType) => {
    return activityTypes.find(t => t.value === activityType)?.icon || <NoteAddOutlined />;
  };

  const getTypeLabel = (activityType: ActivityType) => {
    return activityTypes.find(t => t.value === activityType)?.label || 'Note';
  };

  return (
    <Paper
      component="form"
      onSubmit={handleSubmit}
      elevation={compact ? 0 : 2}
      sx={{
        p: compact ? 2 : 3,
        backgroundColor: compact ? 'grey.50' : 'background.paper',
      }}
    >
      <Stack spacing={2}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            {getTypeIcon(type)}
            <Typography variant="h6">
              Add {getTypeLabel(type)}
            </Typography>
          </Stack>
          {onCancel && (
            <IconButton size="small" onClick={handleCancel} disabled={isSubmitting}>
              <CloseOutlined />
            </IconButton>
          )}
        </Stack>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Activity Type Selector */}
        {showTypeSelector && (
          <FormControl fullWidth size="small">
            <InputLabel>Activity Type 活動類型</InputLabel>
            <Select
              value={type}
              label="Activity Type 活動類型"
              onChange={(e) => setType(e.target.value as ActivityType)}
              disabled={isSubmitting}
            >
              {activityTypes.map((actType) => (
                <MenuItem key={actType.value} value={actType.value}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {actType.icon}
                    <Typography>{actType.label}</Typography>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Subject (Optional for all types) */}
        <TextField
          label="Subject 主旨 (Optional)"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          disabled={isSubmitting}
          size="small"
          fullWidth
          placeholder="Brief subject or title..."
        />

        {/* Type-specific fields */}
        {type === 'EMAIL' && (
          <>
            <TextField
              label="Email Subject 電郵主旨 *"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              disabled={isSubmitting}
              size="small"
              fullWidth
              required
              placeholder="Enter email subject..."
            />

            {/* AI Generate Email Button */}
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<AutoAwesome />}
              onClick={() => setEmailBuilderOpen(true)}
              disabled={isSubmitting}
              sx={{ alignSelf: 'flex-start' }}
            >
              AI Generate Email 生成電郵
            </Button>
          </>
        )}

        {type === 'CALL' && (
          <TextField
            label="Call Duration 通話時長 (minutes)"
            type="number"
            value={callDuration}
            onChange={(e) => setCallDuration(e.target.value ? Number(e.target.value) : '')}
            disabled={isSubmitting}
            size="small"
            fullWidth
            placeholder="E.g., 15"
            inputProps={{ min: 1, max: 999 }}
          />
        )}

        {type === 'MEETING' && (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              label="Meeting Date & Time 會議日期時間 *"
              value={meetingDate}
              onChange={(newValue) => setMeetingDate(newValue)}
              disabled={isSubmitting}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  required: true,
                },
              }}
            />
          </LocalizationProvider>
        )}

        {/* Content */}
        <TextField
          label={`Content 內容 *`}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          disabled={isSubmitting}
          multiline
          rows={compact ? 3 : 4}
          fullWidth
          required
          placeholder={placeholder}
          inputProps={{ maxLength: 2000 }}
          helperText={`${content.length}/2000 characters`}
        />

        {/* Quick Templates (for NOTE type) */}
        {type === 'NOTE' && !content && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Quick templates 快速範本:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                label="Follow-up needed 需要跟進"
                size="small"
                onClick={() => setContent('Follow-up needed. Action required: ')}
                disabled={isSubmitting}
              />
              <Chip
                label="Client interested 客戶有興趣"
                size="small"
                onClick={() => setContent('Client showed interest in our products/services. Next step: ')}
                disabled={isSubmitting}
              />
              <Chip
                label="No response 無回應"
                size="small"
                onClick={() => setContent('No response received. Will try again on: ')}
                disabled={isSubmitting}
              />
              <Chip
                label="Meeting scheduled 已安排會議"
                size="small"
                onClick={() => setContent('Meeting scheduled for: ')}
                disabled={isSubmitting}
              />
            </Stack>
          </Box>
        )}

        {/* Actions */}
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          {onCancel && (
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel 取消
            </Button>
          )}
          <Button
            type="submit"
            variant="contained"
            startIcon={<SendOutlined />}
            disabled={isSubmitting || !content.trim()}
          >
            {isSubmitting ? 'Adding... 新增中...' : `Add ${getTypeLabel(type)}`}
          </Button>
        </Stack>
      </Stack>

      {/* Email Builder Dialog */}
      <EmailBuilderDialog
        open={emailBuilderOpen}
        onClose={() => setEmailBuilderOpen(false)}
        onUseEmail={handleUseGeneratedEmail}
        recipientContext={recipientContext}
      />
    </Paper>
  );
};

export default AddNoteForm;
